// DataManagementService-DependencyFree.js - Dependency-free data management for JobEng app
// Uses only AsyncStorage and built-in React Native features

import { AsyncStorage } from 'react-native';

// Fallback for newer React Native versions
let storage;
try {
  storage = AsyncStorage;
} catch (e) {
  // For newer RN versions, try to import from community package if available
  try {
    const AsyncStorageModule = require('@react-native-async-storage/async-storage');
    storage = AsyncStorageModule.default || AsyncStorageModule;
  } catch (e2) {
    console.warn('AsyncStorage not available, using memory storage');
    // Fallback to memory storage
    const memoryStorage = {};
    storage = {
      getItem: async (key) => memoryStorage[key] || null,
      setItem: async (key, value) => { memoryStorage[key] = value; },
      removeItem: async (key) => { delete memoryStorage[key]; },
      multiGet: async (keys) => keys.map(key => [key, memoryStorage[key] || null]),
      multiSet: async (keyValuePairs) => {
        keyValuePairs.forEach(([key, value]) => { memoryStorage[key] = value; });
      },
      multiRemove: async (keys) => {
        keys.forEach(key => { delete memoryStorage[key]; });
      }
    };
  }
}

export class DataManagementService {
  constructor() {
    this.isOnline = true;
    this.syncInProgress = false;
    this.listeners = [];
    this.syncQueue = [];
    this.autoSyncEnabled = true;
    this.syncInterval = 5 * 60 * 1000; // 5 minutes
    this.syncTimer = null;
    
    // Storage keys
    this.STORAGE_KEYS = {
      // User Data
      USER_DATA: 'user_data',
      USER_TYPE: 'user_type',
      AUTH_TOKEN: 'auth_token',
      
      // Business Data
      BUSINESS_PROFILE: 'business_profile',
      PROJECTS: 'projects',
      PARTNERS: 'partners',
      INTERVIEWS: 'interviews',
      JOBS: 'jobs',
      CANDIDATES: 'candidates',
      ANALYTICS: 'analytics',
      
      // Professional Data
      PROFESSIONAL_PROFILE: 'professional_profile',
      APPLICATIONS: 'applications',
      PORTFOLIO: 'portfolio',
      RESUME: 'resume',
      
      // Communications
      MESSAGES: 'messages',
      NOTIFICATIONS: 'notifications',
      
      // Settings
      APP_SETTINGS: 'app_settings',
      SYNC_TIMESTAMP: 'sync_timestamp',
      OFFLINE_QUEUE: 'offline_queue',
      SYNC_PREFERENCES: 'sync_preferences',
    };

    // Network detection using simple fetch test
    this.checkNetworkStatus();
    this.startNetworkMonitoring();
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('DataManagementService initializing (dependency-free)...');
      
      // Load sync preferences
      await this.loadSyncPreferences();
      
      // Load offline queue
      await this.loadSyncQueue();
      
      // Start auto-sync if enabled
      if (this.autoSyncEnabled) {
        this.startAutoSync();
      }

      console.log('DataManagementService initialized successfully (dependency-free)');
      return { success: true };
    } catch (error) {
      console.error('DataManagementService initialization error:', error);
      return { success: false, error: error.message };
    }
  }

  // ===========================================
  // NETWORK MONITORING (Dependency-Free)
  // ===========================================

  async checkNetworkStatus() {
    try {
      // Simple network check using fetch with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      });
      
      clearTimeout(timeoutId);
      this.isOnline = true;
    } catch (error) {
      this.isOnline = false;
    }
    
    this.notifyListeners('network_status_changed', { isOnline: this.isOnline });
  }

  startNetworkMonitoring() {
    // Check network status every 30 seconds
    setInterval(() => {
      this.checkNetworkStatus();
    }, 30000);
  }

  // ===========================================
  // LISTENER MANAGEMENT
  // ===========================================

  addListener(event, callback) {
    this.listeners.push({ event, callback });
  }

  removeListener(event, callback) {
    this.listeners = this.listeners.filter(
      listener => listener.event !== event || listener.callback !== callback
    );
  }

  removeAllListeners() {
    this.listeners = [];
  }

  notifyListeners(event, data) {
    this.listeners
      .filter(listener => listener.event === event)
      .forEach(listener => {
        try {
          listener.callback(data);
        } catch (error) {
          console.error('Listener callback error:', error);
        }
      });
  }

  // ===========================================
  // SYNC MANAGEMENT
  // ===========================================

  async loadSyncPreferences() {
    try {
      const preferences = await storage.getItem(this.STORAGE_KEYS.SYNC_PREFERENCES);
      if (preferences) {
        const parsed = JSON.parse(preferences);
        this.autoSyncEnabled = parsed.autoSyncEnabled !== false;
        this.syncInterval = parsed.syncInterval || this.syncInterval;
      }
    } catch (error) {
      console.error('Error loading sync preferences:', error);
    }
  }

  async saveSyncPreferences() {
    try {
      const preferences = {
        autoSyncEnabled: this.autoSyncEnabled,
        syncInterval: this.syncInterval,
      };
      await storage.setItem(this.STORAGE_KEYS.SYNC_PREFERENCES, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving sync preferences:', error);
    }
  }

  startAutoSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }

    this.syncTimer = setInterval(() => {
      if (this.isOnline && !this.syncInProgress) {
        this.processSyncQueue();
      }
    }, this.syncInterval);
  }

  stopAutoSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  // ===========================================
  // DATA OPERATIONS
  // ===========================================

  // Get data with fallback strategy
  async getData(key, defaultValue = null) {
    try {
      const cachedData = await storage.getItem(key);
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      return defaultValue;
    } catch (error) {
      console.error('Error getting data:', error);
      return defaultValue;
    }
  }

  // Set data with sync strategy
  async setData(key, data, syncImmediately = false) {
    try {
      // Always save locally first
      await storage.setItem(key, JSON.stringify(data));

      // Add to sync queue if online sync is needed
      if (syncImmediately && this.isOnline) {
        await this.addToSyncQueue(key, data, 'UPDATE');
        await this.processSyncQueue();
      } else {
        // Add to offline queue
        await this.addToSyncQueue(key, data, 'UPDATE');
      }

      this.notifyListeners('data_updated', { key, data });
      return { success: true };
    } catch (error) {
      console.error('Error setting data:', error);
      return { success: false, error: error.message };
    }
  }

  // ===========================================
  // SYNC QUEUE MANAGEMENT
  // ===========================================

  async addToSyncQueue(key, data, operation) {
    try {
      const queueItem = {
        id: Date.now().toString(),
        key,
        data,
        operation,
        timestamp: new Date().toISOString(),
        retryCount: 0,
        maxRetries: 3,
      };

      this.syncQueue.push(queueItem);
      
      // Persist queue to storage
      await storage.setItem(this.STORAGE_KEYS.OFFLINE_QUEUE, JSON.stringify(this.syncQueue));
      
      this.notifyListeners('sync_queue_updated', { queue: this.syncQueue });
      return { success: true };
    } catch (error) {
      console.error('Error adding to sync queue:', error);
      return { success: false, error: error.message };
    }
  }

  async processSyncQueue() {
    if (this.syncInProgress || !this.isOnline || this.syncQueue.length === 0) {
      return { success: true, processed: 0 };
    }

    this.syncInProgress = true;
    this.notifyListeners('sync_status_changed', 'syncing');

    let processed = 0;
    const failedItems = [];

    try {
      // Simple sync simulation (replace with actual API calls)
      for (const item of [...this.syncQueue]) {
        try {
          // Simulate API call
          await this.simulateAPICall(item);
          
          // Remove from queue on success
          this.syncQueue = this.syncQueue.filter(queueItem => queueItem.id !== item.id);
          processed++;
        } catch (error) {
          console.error('Error processing sync queue item:', error);
          item.retryCount++;
          if (item.retryCount >= item.maxRetries) {
            this.syncQueue = this.syncQueue.filter(queueItem => queueItem.id !== item.id);
            failedItems.push(item);
          }
        }
      }

      // Update persisted queue
      await storage.setItem(this.STORAGE_KEYS.OFFLINE_QUEUE, JSON.stringify(this.syncQueue));
      
      // Update sync timestamp
      await storage.setItem(this.STORAGE_KEYS.SYNC_TIMESTAMP, new Date().toISOString());

      this.notifyListeners('sync_queue_updated', { queue: this.syncQueue });
      
      return { success: true, processed, failed: failedItems.length };
    } catch (error) {
      console.error('Error processing sync queue:', error);
      return { success: false, error: error.message };
    } finally {
      this.syncInProgress = false;
      this.notifyListeners('sync_status_changed', 'synced');
    }
  }

  async simulateAPICall(item) {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Simulate occasional failures for testing
    if (Math.random() < 0.1) { // 10% failure rate
      throw new Error('Simulated API failure');
    }
    
    console.log(`Synced ${item.operation} for ${item.key}`);
  }

  async loadSyncQueue() {
    try {
      const queueData = await storage.getItem(this.STORAGE_KEYS.OFFLINE_QUEUE);
      if (queueData) {
        this.syncQueue = JSON.parse(queueData);
      }
    } catch (error) {
      console.error('Error loading sync queue:', error);
    }
  }

  async clearSyncQueue() {
    try {
      this.syncQueue = [];
      await storage.removeItem(this.STORAGE_KEYS.OFFLINE_QUEUE);
      this.notifyListeners('sync_queue_updated', { queue: this.syncQueue });
      return { success: true };
    } catch (error) {
      console.error('Error clearing sync queue:', error);
      return { success: false, error: error.message };
    }
  }

  getSyncQueue() {
    return [...this.syncQueue];
  }

  // ===========================================
  // BUSINESS-SPECIFIC METHODS
  // ===========================================

  // Jobs
  async getJobs(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.JOBS, []);
  }

  async createJob(jobData) {
    const job = {
      id: jobData.id || `job_${Date.now()}`,
      ...jobData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    const jobs = await this.getJobs();
    jobs.push(job);
    
    await this.setData(this.STORAGE_KEYS.JOBS, jobs);
    this.notifyListeners('jobs_updated', jobs);
    
    return { success: true, job };
  }

  async updateJob(jobId, updateData) {
    const jobs = await this.getJobs();
    const index = jobs.findIndex(job => job.id === jobId);
    
    if (index === -1) {
      return { success: false, error: 'Job not found' };
    }

    jobs[index] = {
      ...jobs[index],
      ...updateData,
      updated_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    await this.setData(this.STORAGE_KEYS.JOBS, jobs);
    this.notifyListeners('jobs_updated', jobs);
    
    return { success: true, job: jobs[index] };
  }

  async deleteJob(jobId) {
    const jobs = await this.getJobs();
    const filteredJobs = jobs.filter(job => job.id !== jobId);
    
    if (jobs.length === filteredJobs.length) {
      return { success: false, error: 'Job not found' };
    }

    await this.setData(this.STORAGE_KEYS.JOBS, filteredJobs);
    this.notifyListeners('jobs_updated', filteredJobs);
    
    return { success: true };
  }

  // Projects
  async getProjects(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.PROJECTS, []);
  }

  async createProject(projectData) {
    const project = {
      id: projectData.id || `project_${Date.now()}`,
      ...projectData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    const projects = await this.getProjects();
    projects.push(project);
    
    await this.setData(this.STORAGE_KEYS.PROJECTS, projects);
    this.notifyListeners('projects_updated', projects);
    
    return { success: true, project };
  }

  // Partners
  async getPartners(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.PARTNERS, []);
  }

  async createPartner(partnerData) {
    const partner = {
      id: partnerData.id || `partner_${Date.now()}`,
      ...partnerData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    const partners = await this.getPartners();
    partners.push(partner);
    
    await this.setData(this.STORAGE_KEYS.PARTNERS, partners);
    this.notifyListeners('partners_updated', partners);
    
    return { success: true, partner };
  }

  // Interviews
  async getInterviews(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.INTERVIEWS, []);
  }

  async scheduleInterview(interviewData) {
    const interview = {
      id: interviewData.id || `interview_${Date.now()}`,
      ...interviewData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    const interviews = await this.getInterviews();
    interviews.push(interview);
    
    await this.setData(this.STORAGE_KEYS.INTERVIEWS, interviews);
    this.notifyListeners('interviews_updated', interviews);
    
    return { success: true, interview };
  }

  // Messages
  async getMessages(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.MESSAGES, []);
  }

  async sendMessage(messageData) {
    const message = {
      id: messageData.id || `message_${Date.now()}`,
      ...messageData,
      sent_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    const messages = await this.getMessages();
    messages.push(message);
    
    await this.setData(this.STORAGE_KEYS.MESSAGES, messages, true); // Sync immediately
    this.notifyListeners('messages_updated', messages);
    
    return { success: true, message };
  }

  // Notifications
  async getNotifications(forceRefresh = false) {
    return await this.getData(this.STORAGE_KEYS.NOTIFICATIONS, []);
  }

  // ===========================================
  // ANALYTICS (Simplified)
  // ===========================================

  async getJobAnalytics(userId) {
    try {
      const jobs = await this.getJobs();
      const userJobs = jobs.filter(job => job.user_id === userId);
      
      const analytics = {
        totalJobs: userJobs.length,
        activeJobs: userJobs.filter(job => job.status === 'active').length,
        totalApplicants: userJobs.reduce((sum, job) => sum + (job.applicants || 0), 0),
        totalViews: userJobs.reduce((sum, job) => sum + (job.views || 0), 0),
        pendingSync: this.syncQueue.length
      };

      // Calculate conversion rate
      analytics.conversionRate = analytics.totalViews > 0 
        ? Math.round((analytics.totalApplicants / analytics.totalViews) * 100) 
        : 0;

      return { success: true, data: analytics };
    } catch (error) {
      console.error('Error getting job analytics:', error);
      return { success: false, error: error.message };
    }
  }

  // ===========================================
  // UTILITY METHODS
  // ===========================================

  async getLastSyncTime() {
    try {
      const timestamp = await storage.getItem(this.STORAGE_KEYS.SYNC_TIMESTAMP);
      return timestamp ? new Date(timestamp) : null;
    } catch (error) {
      console.error('Error getting last sync time:', error);
      return null;
    }
  }

  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      queueLength: this.syncQueue.length,
      autoSyncEnabled: this.autoSyncEnabled,
    };
  }

  async setAutoSync(enabled) {
    this.autoSyncEnabled = enabled;
    
    if (enabled) {
      this.startAutoSync();
    } else {
      this.stopAutoSync();
    }
    
    await this.saveSyncPreferences();
  }

  async clearAllData() {
    try {
      // Clear AsyncStorage
      const keys = Object.values(this.STORAGE_KEYS);
      await storage.multiRemove(keys);

      // Clear sync queue
      await this.clearSyncQueue();

      this.notifyListeners('all_data_cleared', {});
      return { success: true };
    } catch (error) {
      console.error('Error clearing all data:', error);
      return { success: false, error: error.message };
    }
  }

  // Cleanup method
  async cleanup() {
    try {
      this.stopAutoSync();
      this.removeAllListeners();
      return { success: true };
    } catch (error) {
      console.error('Error during cleanup:', error);
      return { success: false, error: error.message };
    }
  }
}
